import { FieldInput, Form } from '@isbj-redesign/final-form-components'
import { Alert, Color, Modal, ModalBody, ModalHeader, ModalProps } from '@isbj-redesign/frontend-components'
import AnfragenIcon from '@isbj-redesign/icons/FaFileExportSolid'
import dayjs from 'dayjs'
import filesize from 'filesize'
import React, { useCallback, useMemo, useState } from 'react'
import * as yup from 'yup'
import { useMutationBereitstellen } from './Api'
import FileInfo from './FileInfo'
import ModalActions from './ModalActions'
import SpinningButton from './SpinningButton'
import { Dokument } from './types'

export type Props = {
    selected: Dokument[]
    resetSelected: () => void
    fullSize: number
} & ModalProps

export function SammelanfrageModal({ selected, resetSelected, fullSize, onClose, ...modalProps }: Props) {
    const onSuccess = useCallback(() => {
        resetSelected()
        onClose && onClose()
    }, [resetSelected, onClose])

    const onAbort = useCallback(() => onClose && onClose(), [onClose])

    return (
        <Modal {...modalProps} onClose={onClose} preventManualClose>
            {modalProps.show && <SammelanfrageForm selected={selected} fullSize={fullSize} onSuccess={onSuccess} onAbort={onAbort} />}
        </Modal>
    )
}

type SammelanfrageFormValues = {
    dateiname: string
}

type SammelanfrageFormProps = {
    selected: Dokument[]
    fullSize: number
    onSuccess(): void
    onAbort(): void
}

function SammelanfrageForm({ selected, fullSize, onSuccess, onAbort }: SammelanfrageFormProps) {
    const { isLoading, mutate, isError, error, reset } = useMutationBereitstellen()
    const initialValues = useMemo<SammelanfrageFormValues>(() => ({ dateiname: `SammelBereitstellung_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}` }), [])

    const onAbortInternal = useCallback(() => {
        reset()
        onAbort()
    }, [reset, onAbort])

    const onSubmit = useCallback(
        ({ dateiname }: SammelanfrageFormValues) => {
            mutate(
                { dokumente: selected, dateiname },
                {
                    onError: () => undefined,
                    onSuccess: () => {
                        reset()
                        onSuccess()
                    },
                }
            )
        },
        [mutate, selected, reset, onSuccess]
    )

    return (
        <Form validationSchema={schema} initialValues={initialValues} onSubmit={onSubmit}>
            <ModalHeader title="Auswahl anfragen" />
            <ModalBody>
                {isError && <Alert color={Color.Danger}>{error?.message}</Alert>}
                <p>Die folgende Auswahl ({filesize(fullSize)}) anfragen?</p>
                <FieldInput name="dateiname" label="Dateiname" maxLength={150} required />
                <div className="m-1">
                    {selected.map((dokument) => (
                        <FileInfo key={dokument.id} name={dokument.name} typ={dokument.typ} groesse={dokument.groesse} className="mb-3" />
                    ))}
                </div>
            </ModalBody>
            <ModalActions onClose={onAbortInternal}>
                <SpinningButton icon={AnfragenIcon} label={isLoading ? 'Wird angefragt' : 'Anfragen'} spinning={isLoading} />
            </ModalActions>
        </Form>
    )
}

const schema = yup.object({
    dateiname: yup.string().required('Bitte geben Sie einen Dateinamen an.'),
})

type ModalDataResult<Entity = unknown, Key = string> = {
    isVisible: boolean
    data?: Entity
    key?: Key
    toggleModal: () => void
    openModal: (data?: Entity, key?: Key) => void
    closeModal: () => void
}

export function useModal<Entity, Key = string>(): ModalDataResult<Entity, Key> {
    const [modalData, setModalData] = useState<{ key?: Key; data?: Entity; isVisible: boolean }>({
        isVisible: false,
    })

    const toggleModal = useCallback(() => {
        setModalData((o) => ({ ...o, isVisible: !o.isVisible }))
    }, [setModalData])

    const openModal = useCallback<(data?: Entity, key?: Key) => void>(
        (data, key) => {
            setModalData({ isVisible: true, data, key })
        },
        [setModalData]
    )

    const closeModal = useCallback(() => {
        setModalData({ isVisible: false })
    }, [setModalData])

    return {
        ...modalData,
        toggleModal,
        openModal,
        closeModal,
    }
}
